import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { useNotification } from '../components/Notification';
import { config } from '../config';
import DataTable from '../components/DataTable';
import IconButton from '../components/IconButton';
import './AdminDashboard.css';

function AdminUsers() {
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [teams, setTeams] = useState([]);
  const [roles, setRoles] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [passwordChangeUser, setPasswordChangeUser] = useState(null);
  const [passwordFormData, setPasswordFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    role: 'user',
    tenantId: '',
    roleId: '',
    teamId: '',
    avatar: null
  });

  const loadUser = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.API_URL}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setCurrentUser(data);
    } catch (error) {
      console.error('Error loading user:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.API_URL}/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Fehler beim Laden der Benutzer' }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }
      
      const data = await response.json();
      setUsers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading users:', error);
      showNotification(error.message || 'Fehler beim Laden der Benutzer', 'error');
      setUsers([]);
    }
  };

  const loadTenants = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.API_URL}/admin/tenants`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        console.error('Failed to load tenants:', response.status);
        setTenants([]);
        return;
      }
      
      const data = await response.json();
      setTenants(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading tenants:', error);
      setTenants([]);
    }
  };

  const loadRoles = async (tenantId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.API_URL}/roles`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Id': tenantId
        }
      });
      
      if (!response.ok) {
        console.error('Failed to load roles:', response.status);
        setRoles([]);
        return;
      }
      
      const data = await response.json();
      setRoles(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading roles:', error);
      setRoles([]);
    }
  };

  const loadTeams = async (tenantId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.API_URL}/teams`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Id': tenantId
        }
      });
      
      if (!response.ok) {
        console.error('Failed to load teams:', response.status);
        setTeams([]);
        return;
      }
      
      const data = await response.json();
      setTeams(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading teams:', error);
      setTeams([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const url = editingUser 
        ? `${config.API_URL}/users/${editingUser.id}`
        : `${config.API_URL}/users`;
      
      const method = editingUser ? 'PUT' : 'POST';
      // Beim Erstellen: immer aktueller Tenant, beim Bearbeiten: bestehender Tenant
      const finalTenantId = editingUser 
        ? (isTenantAdmin ? currentUser?.tenantId : formData.tenantId)
        : (isTenantAdmin 
            ? currentUser?.tenantId 
            : (localStorage.getItem('tenantId') || currentUser?.tenantId || formData.tenantId));
      const body = editingUser && !formData.password 
        ? { 
            username: formData.username, 
            role: formData.role, 
            tenantId: finalTenantId,
            roleId: formData.roleId || null,
            teamId: formData.teamId || null,
            avatar: formData.avatar || null
          }
        : {
            username: formData.username,
            password: formData.password,
            role: formData.role,
            tenantId: finalTenantId,
            roleId: formData.roleId || null,
            teamId: formData.teamId || null,
            avatar: formData.avatar || null
          };
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Fehler beim Speichern');
      }

      setShowForm(false);
      setEditingUser(null);
      setFormData({
        username: '',
        password: '',
        role: 'user',
        tenantId: '',
        roleId: '',
        teamId: '',
        avatar: null
      });
      setAvatarPreview(null);
      
      const userId = searchParams.get('userId');
      if (userId) {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('userId');
        setSearchParams(newSearchParams, { replace: true });
      }
      
      loadUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      showNotification(error.message || 'Fehler beim Speichern des Benutzers', 'error');
    }
  };

  const handleAvatarUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showNotification('Bitte wählen Sie eine Bilddatei aus', 'error');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      showNotification('Die Datei ist zu groß. Maximal 2MB erlaubt.', 'error');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result;
      setFormData({ ...formData, avatar: base64String });
      setAvatarPreview(base64String);
    };
    reader.onerror = () => {
      showNotification('Fehler beim Lesen der Datei', 'error');
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveAvatar = () => {
    setFormData({ ...formData, avatar: null });
    setAvatarPreview(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Benutzer wirklich löschen?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.API_URL}/users/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Fehler beim Löschen');
      }

      loadUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      showNotification('Fehler beim Löschen des Benutzers', 'error');
    }
  };

  const handleChangePassword = (user) => {
    setPasswordChangeUser(user);
    setPasswordFormData({
      newPassword: '',
      confirmPassword: ''
    });
    setShowPasswordForm(true);
  };

  const handleResendVerification = async (user) => {
    if (!window.confirm(`Bestätigungsmail an ${user.username} erneut versenden?`)) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.API_URL}/users/${user.id}/resend-verification`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Fehler beim Versenden der E-Mail');
      }

      showNotification('Bestätigungsmail wurde erneut versendet', 'success');
    } catch (error) {
      console.error('Error resending verification email:', error);
      showNotification(error.message || 'Fehler beim Versenden der E-Mail', 'error');
    }
  };

  const handleVerifyEmail = async (user) => {
    if (!window.confirm(`E-Mail-Adresse von ${user.username} manuell als bestätigt markieren?`)) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.API_URL}/users/${user.id}/verify-email`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Fehler beim Bestätigen der E-Mail');
      }

      showNotification('E-Mail-Adresse wurde als bestätigt markiert', 'success');
      loadUsers();
    } catch (error) {
      console.error('Error verifying email:', error);
      showNotification(error.message || 'Fehler beim Bestätigen der E-Mail', 'error');
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (passwordFormData.newPassword !== passwordFormData.confirmPassword) {
      showNotification('Die Passwörter stimmen nicht überein', 'error');
      return;
    }

    if (passwordFormData.newPassword.length < 6) {
      showNotification('Das Passwort muss mindestens 6 Zeichen lang sein', 'error');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.API_URL}/users/${passwordChangeUser.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          password: passwordFormData.newPassword
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Fehler beim Ändern des Passworts');
      }

      setShowPasswordForm(false);
      setPasswordChangeUser(null);
      setPasswordFormData({
        newPassword: '',
        confirmPassword: ''
      });
      loadUsers();
    } catch (error) {
      console.error('Error changing password:', error);
      showNotification(error.message || 'Fehler beim Ändern des Passworts', 'error');
    }
  };

  const getRoleLabel = (role) => {
    const labels = {
      admin: 'Admin',
      tenantAdmin: 'Tenant Admin',
      user: 'Benutzer'
    };
    return labels[role] || role;
  };

  const getTenantName = (tenantId) => {
    if (!tenantId) return '-';
    if (!Array.isArray(tenants)) return tenantId;
    const tenant = tenants.find(t => t.id === tenantId);
    return tenant ? tenant.name : tenantId;
  };

  const isTenantAdmin = currentUser?.role === 'tenantAdmin';

  const handleEdit = (user) => {
    setEditingUser(user);
    // Wenn TenantAdmin, immer den eigenen tenantId verwenden
    const tenantId = isTenantAdmin && currentUser?.tenantId 
      ? currentUser.tenantId 
      : (user.tenantId || '');
    
    setFormData({
      username: user.username || '',
      password: '',
      role: user.role || 'user',
      tenantId: tenantId,
      roleId: user.roleId || '',
      teamId: user.teamId || '',
      avatar: user.avatar || null
    });
    setAvatarPreview(user.avatar || null);
    if (tenantId) {
      loadTeams(tenantId);
      loadRoles(tenantId);
    }
    setShowForm(true);
  };

  useEffect(() => {
    loadUser();
    loadUsers();
    loadTenants();
  }, []);

  useEffect(() => {
    // Wenn TenantAdmin, automatisch tenantId setzen
    if (currentUser?.role === 'tenantAdmin' && currentUser?.tenantId) {
      setFormData(prev => ({
        ...prev,
        tenantId: currentUser.tenantId
      }));
      loadTeams(currentUser.tenantId);
      loadRoles(currentUser.tenantId);
    }
  }, [currentUser]);

  useEffect(() => {
    const userId = searchParams.get('userId');
    if (userId && users.length > 0 && !editingUser) {
      const user = users.find(u => u.id === userId);
      if (user) {
        handleEdit(user);
      }
    }
  }, [searchParams, users, editingUser]);

  useEffect(() => {
    if (formData.tenantId) {
      loadTeams(formData.tenantId);
      loadRoles(formData.tenantId);
    }
  }, [formData.tenantId]);

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <h1>Benutzer verwalten</h1>
        <div className="admin-header-actions">
          <button type="button" onClick={() => navigate('/admin')} className="btn-secondary">
            ← Zurück
          </button>
          <button type="button" onClick={() => {
            try {
              const isTenantAdminLocal = currentUser?.role === 'tenantAdmin';
              const isAdmin = currentUser?.role === 'admin';
              // Für Admins: tenantId leer lassen, damit Dropdown sichtbar ist
              // Für TenantAdmins: eigenen tenantId verwenden
              const targetTenantId = isTenantAdminLocal 
                ? currentUser?.tenantId 
                : (isAdmin ? '' : (localStorage.getItem('tenantId') || currentUser?.tenantId || ''));
              
              setFormData(prev => ({
                ...prev,
                username: '',
                password: '',
                role: 'user',
                tenantId: targetTenantId,
                roleId: '',
                teamId: '',
                avatar: null
              }));
              setEditingUser(null);
              setAvatarPreview(null);
              
              if (targetTenantId) {
                loadTeams(targetTenantId);
                loadRoles(targetTenantId);
              }
              setShowForm(true);
            } catch (error) {
              console.error('Error opening new user form:', error);
              showNotification('Fehler beim Öffnen des Formulars', 'error');
            }
          }} className="btn-primary">
            Neuer Benutzer
          </button>
        </div>
      </div>

      {showPasswordForm && (
        <div className="modal">
          <div className="modal-content">
            <h2>Passwort ändern für {passwordChangeUser?.username}</h2>
            <form onSubmit={handlePasswordSubmit}>
              <div className="form-group">
                <label>Neues Passwort</label>
                <input
                  type="password"
                  value={passwordFormData.newPassword}
                  onChange={(e) => setPasswordFormData({ ...passwordFormData, newPassword: e.target.value })}
                  required
                  minLength={6}
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>Passwort bestätigen</label>
                <input
                  type="password"
                  value={passwordFormData.confirmPassword}
                  onChange={(e) => setPasswordFormData({ ...passwordFormData, confirmPassword: e.target.value })}
                  required
                  minLength={6}
                />
              </div>
              <div className="form-actions">
                <button type="submit" className="btn-primary">
                  Passwort ändern
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordForm(false);
                    setPasswordChangeUser(null);
                    setPasswordFormData({
                      newPassword: '',
                      confirmPassword: ''
                    });
                  }}
                  className="btn-secondary"
                >
                  Abbrechen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showForm && (
        <div className="modal">
          <div className="modal-content">
            <h2>{editingUser ? 'Benutzer bearbeiten' : 'Neuer Benutzer'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Benutzername</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                />
              </div>
              {(!editingUser || formData.password) && (
                <div className="form-group">
                  <label>Passwort{editingUser ? ' (leer lassen zum Beibehalten)' : ''}</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required={!editingUser}
                  />
                </div>
              )}
              {!isTenantAdmin && (
                <div className="form-group">
                  <label>Rolle</label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  >
                    {currentUser?.role === 'admin' && <option value="admin">Admin</option>}
                    <option value="tenantAdmin">Tenant Admin</option>
                    <option value="user">Benutzer</option>
                  </select>
                </div>
              )}
              {formData.role !== 'admin' && currentUser?.role === 'admin' && (
                <div className="form-group">
                  <label>Tenant</label>
                  <select
                    value={formData.tenantId}
                    onChange={(e) => {
                      const selectedTenantId = e.target.value;
                      setFormData({ ...formData, tenantId: selectedTenantId });
                      if (selectedTenantId) {
                        loadTeams(selectedTenantId);
                        loadRoles(selectedTenantId);
                      }
                    }}
                    required
                  >
                    <option value="">Bitte wählen</option>
                    {Array.isArray(tenants) && tenants.map(tenant => (
                      <option key={tenant.id} value={tenant.id}>
                        {tenant.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {formData.role !== 'admin' && isTenantAdmin && (
                <input 
                  type="hidden" 
                  name="tenantId" 
                  value={currentUser?.tenantId || ''} 
                />
              )}
              {formData.role !== 'admin' && formData.tenantId && (
                <>
                  <div className="form-group">
                    <label>Rolle</label>
                    <select
                      value={formData.roleId}
                      onChange={(e) => setFormData({ ...formData, roleId: e.target.value })}
                    >
                      <option value="">Keine Rolle</option>
                      {Array.isArray(roles) && roles.map(role => (
                        <option key={role.id} value={role.id}>
                          {role.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Team</label>
                    <select
                      value={formData.teamId}
                      onChange={(e) => setFormData({ ...formData, teamId: e.target.value })}
                    >
                      <option value="">Bitte wählen</option>
                      {Array.isArray(teams) && teams.map(team => (
                        <option key={team.id} value={team.id}>
                          {team.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Avatar</label>
                    {avatarPreview && (
                      <div style={{ marginBottom: '1rem' }}>
                        <img 
                          src={avatarPreview} 
                          alt="Avatar Vorschau" 
                          style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '50%', border: '1px solid #ddd' }}
                        />
                        <button
                          type="button"
                          onClick={handleRemoveAvatar}
                          className="btn-secondary btn-small"
                          style={{ marginLeft: '1rem' }}
                        >
                          Entfernen
                        </button>
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleAvatarUpload}
                    />
                    <p style={{ fontSize: '0.875rem', color: '#666', marginTop: '0.5rem' }}>
                      Max. 2MB, empfohlene Formate: PNG, JPG
                    </p>
                  </div>
                </>
              )}
              <div className="form-actions">
                <button type="submit" className="btn-primary">
                  {editingUser ? 'Speichern' : 'Erstellen'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingUser(null);
                    setFormData({
                      username: '',
                      password: '',
                      role: 'user',
                      tenantId: '',
                      roleId: '',
                      teamId: '',
                      avatar: null
                    });
                    setAvatarPreview(null);
                  }}
                  className="btn-secondary"
                >
                  Abbrechen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <DataTable
        data={users}
        columns={[
          {
            key: 'actions',
            header: '',
            sortable: false,
            width: '80px',
            render: (row) => (
              <div style={{ display: 'inline-flex', gap: '0.25rem', whiteSpace: 'nowrap' }}>
                <IconButton
                  icon="edit"
                  onClick={() => handleEdit(row)}
                  title="Bearbeiten"
                />
                {(isTenantAdmin || currentUser?.role === 'admin') && (
                  <IconButton
                    icon="key"
                    onClick={() => handleChangePassword(row)}
                    title="Passwort ändern"
                  />
                )}
                {currentUser?.role === 'admin' && !row.emailVerified && (
                  <>
                    <IconButton
                      icon="mail"
                      onClick={() => handleResendVerification(row)}
                      title="Bestätigungsmail erneut versenden"
                    />
                    <IconButton
                      icon="check"
                      onClick={() => handleVerifyEmail(row)}
                      title="E-Mail als bestätigt markieren"
                    />
                  </>
                )}
                <IconButton
                  icon="delete"
                  onClick={() => handleDelete(row.id)}
                  title="Löschen"
                  className="danger"
                  disabled={row.id === currentUser?.id}
                />
              </div>
            )
          },
          {
            key: 'avatar',
            header: '',
            sortable: false,
            width: '60px',
            align: 'left',
            render: (row) => {
              if (row.avatar) {
                return (
                  <img 
                    src={row.avatar} 
                    alt={row.username} 
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      objectFit: 'cover',
                      border: '1px solid #ddd',
                      display: 'block'
                    }}
                  />
                );
              }
              const initial = (row.username || '?').charAt(0).toUpperCase();
              return (
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: '#4a5568',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1rem',
                    fontWeight: 600,
                    border: '1px solid #ddd',
                    margin: 0
                  }}
                >
                  {initial}
                </div>
              );
            }
          },
          {
            key: 'username',
            header: 'Benutzername',
            sortType: 'string',
            accessor: (row) => row.username || ''
          },
          {
            key: 'role',
            header: 'Rolle',
            sortType: 'string',
            accessor: (row) => getRoleLabel(row.role)
          },
          {
            key: 'tenantId',
            header: 'Tenant',
            sortType: 'string',
            accessor: (row) => getTenantName(row.tenantId)
          },
          {
            key: 'roleId',
            header: 'Rolle',
            sortType: 'string',
            accessor: (row) => {
              // Try to get role name from roles state, fallback to userRole for legacy
              if (row.roleId) {
                const role = Array.isArray(roles) ? roles.find(r => r.id === row.roleId) : null;
                return role ? role.name : (row.userRole || '-');
              }
              return row.userRole || '-';
            }
          },
          {
            key: 'emailVerified',
            header: 'E-Mail bestätigt',
            sortType: 'string',
            accessor: (row) => row.emailVerified ? 'Ja' : 'Nein',
            render: (row) => (
              <span style={{ 
                color: row.emailVerified ? '#28a745' : '#dc3545',
                fontWeight: row.emailVerified ? 'normal' : 'bold'
              }}>
                {row.emailVerified ? '✓ Ja' : '✗ Nein'}
              </span>
            )
          }
        ]}
        defaultSort={{ key: 'username', direction: 'asc' }}
        emptyMessage="Keine Benutzer vorhanden"
      />
    </div>
  );
}

export default AdminUsers;

